'use client'

import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import 'react-circular-progressbar/dist/styles.css'
import { useTranslation } from 'react-i18next'
import {
  FaArrowRight,
  FaTheaterMasks,
  FaTimes,
  FaTrash,
  FaUndo,
  FaCalendar,
  FaChevronDown,
  FaLock,
} from 'react-icons/fa'
import { FiRefreshCw } from 'react-icons/fi'
import { GiDress } from 'react-icons/gi'
import { IoMdHeart } from 'react-icons/io'
import {
  MdChatBubble,
  MdEmojiEmotions,
  MdFaceRetouchingNatural,
  MdRecordVoiceOver,
  MdTransgender,
} from 'react-icons/md'
import { TbSignature } from 'react-icons/tb'
import ClipLoader from 'react-spinners/ClipLoader'
import { useAuth } from '../contexts/AuthContext'
import { useUpgradeGate } from '../contexts/UpgradeGateContext'
import { interestsList, VOICES } from '../helpers/config'
import { getEmotionsString, getOpennessString } from '../helpers/helpers'
import ChatCard from './ChatCard'
import Modal from './Modal'
import ModelModal from './ModelModal'
import { sampleImagesCreate } from '../config/staticImages'

const CharacterInputs = ({
  model,
  setModel,
  handleSubmitPost,
  subscription,
  isLoading,
  activeTab,
  setActiveTab,
  imageSrc,
  setImageSrc,
  charImage,
  setCharImage,

  personality,
  setPersonality,
  interests,
  setInterests,
  name,
  setName,
  occupation,
  sexuality,
  setSexuality,
  setCustomSexuality,
  gender,
  age,
  setAge,
  emotions,
  setEmotions,
  locale,
  faceTab,
  continueOptions,
  setContinueOptions,
  resetStates,
  scenario,
  setScenario,
  count,
  setCountExceededModal,
  openness,
  setOpenness,
  setShowAttributes,
  safe,
  voice,
  setVoice,
  firstMessage,
  setFirstMessage,
}) => {
  const { t } = useTranslation()
  const router = useRouter()

  const [isModelsModalOpen, setIsModelsModalOpen] = useState(false)
  const [clearModalOpen, setClearModalOpen] = useState(false)
  const [creationModalOpen, setCreationModalOpen] = useState(false)
  const [customModalOpen, setCustomModalOpen] = useState(false)
  const [voiceModalOpen, setVoiceModalOpen] = useState(false)
  const [, setAccentModalOpen] = useState(false)

  const [isInterestsModalOpen, setIsInterestsModalOpen] = useState(false)
  const [postModalOpen, setPostModalOpen] = useState(false)
  const [, setCurrentSuggestions] = useState([])
  const [characterModalOpen, setCharacterModalOpen] = useState(false)

  const { user, isPlusOrHigher, isStudioOrHigher } = useAuth()
  const { openUpgrade } = useUpgradeGate()

  useEffect(() => {
    if (gender === 'Man') {
      setShowAttributes(false)
    }
  }, [gender])

  const generateSuggestions = () => {
    const suggestions = new Set()
    while (suggestions.size < 3) {
      const randomIndex = Math.floor(Math.random() * interestsList.length)
      suggestions.add(interestsList[randomIndex])
    }
    setCurrentSuggestions([...suggestions])
  }

  useEffect(() => {
    const interestsArray = interests
      .split(',')
      .map((interest) => interest.trim())
      .filter((interest) => interest !== '')

    if (interestsArray.length > 10) {
      setInterests(interestsArray.slice(0, 10).join(', '))
      setIsInterestsModalOpen(true) // Notify the user
    }
  }, [interests])

  useEffect(() => {
    generateSuggestions()
  }, [])

  return (
    <>
      <Modal
        isOpen={creationModalOpen}
        closeModal={() => setCreationModalOpen(false)}
        title={'Pro Exclusive 🔥'}
        content={
          <p>
            Go Pro to unlock{' '}
            <span className="font-bold">character creation from AI images</span>
            , additional Tokens, and more advanced features!
          </p>
        }
        confirmation="Upgrade"
        onConfirm={() => {
          router.push('/upgrade')
        }}
        confirmation2="Cancel"
        onConfirm2={() => {
          setCreationModalOpen(false)
        }}
      />

      <Modal
        isOpen={customModalOpen}
        closeModal={() => setCustomModalOpen(false)}
        title={'Pro Exclusive 🔥'}
        content={
          <p>
            Go Pro to unlock{' '}
            <span className="font-bold">custom customization options</span>, AI
            characters, additional Tokens, and more advanced features!
          </p>
        }
        confirmation="Upgrade"
        onConfirm={() => {
          router.push('/upgrade')
        }}
        confirmation2="Cancel"
        onConfirm2={() => {
          setCustomModalOpen(false)
        }}
      />

      <ModelModal
        isOpen={isModelsModalOpen}
        closeModal={() => setIsModelsModalOpen(false)}
        subscription={subscription}
        model={model}
        setModel={setModel}
        chat={true}
        user={user}
        safe={safe}
      />

      <Modal
        isOpen={clearModalOpen}
        closeModal={() => setClearModalOpen(false)}
        title="Reset your character"
        content={'This will reset all your progress and settings.'}
        confirmation="Reset"
        confirmation2="Cancel"
        onConfirm={() => {
          resetStates()
          setClearModalOpen(false)
        }}
        onConfirm2={() => setClearModalOpen(false)}
      />

      <Modal
        isOpen={isInterestsModalOpen}
        closeModal={() => setIsInterestsModalOpen(false)}
        title="Maximum Interests"
        content={'You can only enter up to 10 interests.'}
        confirmation="Ok"
      />

      <Modal
        isOpen={postModalOpen}
        closeModal={() => setPostModalOpen(false)}
        title="Ready to chat?"
        content={'Create your character and start chatting.'}
        confirmation="Chat"
        onConfirm={() => handleSubmitPost('private')}
      />

      <Modal
        isOpen={characterModalOpen}
        closeModal={() => setCharacterModalOpen(false)}
        title="Chat"
        content={
          <>
            <img
              src="assets/chat/chatButton.webp"
              alt="Promptchan character example"
              style={{ width: '100%', borderRadius: 8, marginBottom: 12 }}
            />

            <p>
              To use a Promptchan image as a reference, find an image depicting
              your desired character, and press "Chat".
            </p>
          </>
        }
        confirmation="View Explore"
        onConfirm={() => {
          setCharacterModalOpen(false)
          router.push('/explore')
        }}
        confirmation2="View Profile"
        onConfirm2={() => {
          setCharacterModalOpen(false)
          router.push(`/profile/${user?.uid}`)
        }}
      />

      <div className="  bg-[var(--middle-bg-color)] ">
        <div className={`p-3 `}>
          <div className="mb-3 ">
            <div>
              <div className="flex justify-center items-center">
                <div
                  className={` px-5 rounded-l-lg focus:outline-none w-64 flex items-center justify-center`}
                ></div>
                <div
                  className={`px-5 rounded-r-lg focus:outline-none w-64 flex items-center justify-center`}
                ></div>
              </div>

              <div className="">
                <div className="flex items-center rounded-lg mx-auto ">
                  <button
                    type="button"
                    onClick={() => {
                      setClearModalOpen(true)
                    }}
                    className={`font-semibold flex items-center justify-center rounded-full focus:outline-none h-9 w-9 bg-[var(--middle-bg-color)] hover:bg-[var(--foreground-bg-color)] text-white ml-auto text-sm`}
                  >
                    <>
                      <FiRefreshCw />
                    </>
                  </button>
                </div>
              </div>

              <div className="max-w-lg">
                {continueOptions && activeTab === 1 ? (
                  <div className="w-1/2 mx-auto">
                    <div className="cardContainer ">
                      <ChatCard
                        preview={true}
                        name={name}
                        location={locale}
                        age={age}
                        imageUrl={imageSrc}
                        personality={personality}
                        sexuality={sexuality}
                        occupation={occupation}
                        gender={gender}
                        scenario={scenario}
                        emotions={emotions}
                        openness={openness}
                      />
                    </div>
                  </div>
                ) : activeTab === 0 ? (
                  <div className="gap-x-4 width-full">
                    <div className="mb-3 ">
                      <label className="block text-white text-sm font-semibold mb-2 flex items-center">
                        <MdFaceRetouchingNatural
                          color="#FFF"
                          className="mr-2"
                          size={12}
                        />
                        Appearance
                      </label>

                      <div className="items-center flex">
                        <>
                          <div
                            onClick={() => setCharacterModalOpen(true)}
                            className={`w-28 h-28 flex flex-col items-center justify-center px-5 py-5 border-2 border-dashed border-gray-400 rounded-2xl bg-[var(--background-bg-color)] relative focus:outline-none cursor-pointer mr-4 overflow-hidden }`}
                          >
                            <input style={{ display: 'none' }} />

                            {charImage ? (
                              <>
                                <div className="absolute flex items-center justify-center">
                                  <img
                                    src={charImage}
                                    alt="Character Face"
                                    className="object-fill opacity-80"
                                  />
                                </div>
                                {charImage && (
                                  <button
                                    className="absolute text-sm font-semibold z-10 text-red-400 bg-[var(--secondary-bg-color)] p-2 rounded-full text-white top-1 right-1"
                                    onClick={() => setCharImage(null)}
                                  >
                                    <FaTrash />
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <p className="text-white text-center text-xs z-1 mt-1">
                                  Select Character
                                </p>
                              </>
                            )}
                          </div>

                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 w-1/2 mx-2">
                            {sampleImagesCreate.map((image) => (
                              <div
                                key={image.id}
                                onClick={() => {
                                  setCharImage(image.imageUrl)
                                }}
                                className="w-16 h-16 border-2 border-gray-400 
                 rounded-full bg-[var(--middle-bg-color)] 
                 cursor-pointer overflow-hidden relative"
                              >
                                <img
                                  src={image.imageUrl}
                                  alt={image.prompt}
                                  className="absolute inset-0 w-full h-full object-cover"
                                />
                              </div>
                            ))}
                          </div>
                        </>
                      </div>
                      <p className="mt-1 text-gray-400 text-xs">
                        Use an AI Character from Explore or your Profile. Make
                        sure the face is clear for the best results.
                        <span className="text-orange-600"></span>
                      </p>
                    </div>
                  </div>
                ) : activeTab === 1 ? (
                  <div className="gap-x-4 width-full">
                    <div className="mb-3">
                      <label className="block text-white text-sm font-semibold mb-2 flex items-center">
                        <TbSignature color="#FFF" className="mr-2" size={12} />
                        Name
                      </label>

                      <input
                        type="text"
                        id="name"
                        placeholder="Enter a name"
                        value={name}
                        maxLength={30}
                        onChange={(e) => setName(e.target.value)}
                        className="border border-gray-600 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none"
                        required
                      />

                      <p className="mt-1 text-gray-400 text-xs">
                        Select your character's name.
                      </p>
                    </div>

                    <div className="mb-3">
                      <span className="block text-white text-sm font-semibold mb-2 flex items-center">
                        <FaCalendar color="#FFF" className="mr-2" size={12} />
                        {t('age')}
                      </span>

                      <div className="flex items-center space-x-2">
                        <input
                          type="range"
                          min={18}
                          max={80}
                          value={age}
                          onChange={(e) => setAge(parseInt(e.target.value, 10))}
                          className="slider"
                        />

                        <div className="text-white w-24 text-sm">
                          {age} <span className="text-xs">{t('years')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="block text-white text-sm font-semibold mb-2 flex items-center">
                        <MdTransgender
                          color="#FFF"
                          className="mr-2"
                          size={12}
                        />
                        Sexuality
                      </label>
                      {sexuality !== 'Custom' ? (
                        <select
                          id="sexuality"
                          value={sexuality}
                          onChange={(e) => {
                            if (
                              e.target.value === 'Custom' &&
                              !isStudioOrHigher
                            ) {
                              setCustomModalOpen(true)
                            } else {
                              setSexuality(e.target.value)
                            }
                          }}
                          className="border border-gray-500 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)]  focus:outline-none appearance-none text-start cursor-pointer"
                        >
                          <option value="Straight">Straight</option>
                          <option value="Gay">Gay</option>
                          <option value="Lesbian">Lesbian</option>
                          <option value="Custom">Custom</option>
                        </select>
                      ) : (
                        <div className="flex">
                          <input
                            type="text"
                            className="border border-gray-500 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none"
                            placeholder="Enter sexuality"
                            maxLength={30}
                            onChange={(e) => setCustomSexuality(e.target.value)}
                            required
                          />
                          <button
                            onClick={() => {
                              setSexuality('Straight')
                              setCustomSexuality('')
                            }}
                            className="ml-2 text-[var(--foreground-bg-color)]"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      )}

                      <p className="mt-1 text-gray-400 text-xs">
                        Select your character's sexuality.
                      </p>
                    </div>

                    <div className="mb-3">
                      <span className="block text-white text-sm font-semibold mb-2 flex items-center ">
                        <MdRecordVoiceOver
                          color="text-white"
                          className="mr-1"
                          size={12}
                        />
                        Voice{' '}
                        <span className="bg-[var(--main-bg-color)] text-white text-xs font-bold px-2 py-1 ml-1 rounded-full">
                          {t('new')}
                        </span>
                      </span>

                      <div className=" flex items-center ">
                        <div className="relative md:mr-0 mr-2 mx-0.5">
                          <button
                            onClick={() => {
                              setVoiceModalOpen(!voiceModalOpen)
                              setAccentModalOpen(false)
                            }}
                            className="bg-[var(--foreground-bg-color)] p-2 rounded-full text-white flex relative transition-transform duration-200 hover:scale-[1.05] font-semibold flex items-center justify-center "
                          >
                            <span className="md:block hidden mr-2 text-xs">
                              Voice:{' '}
                              {VOICES.find((m) => m.code === voice)?.title ||
                                'Select'}
                            </span>{' '}
                            <FaChevronDown className="md:text-xs" />
                          </button>
                          {voiceModalOpen && (
                            <div className="absolute mt-1 left-0 bg-[#1E1D1E]/95 rounded-xl shadow-lg z-30 w-64 grid grid-cols-2">
                              {VOICES.map((m) => (
                                <div
                                  key={m.code}
                                  onClick={() => {
                                    if (m.plan === 'Plus' && !isPlusOrHigher) {
                                      // Was a hard /upgrade navigation that abandoned the
                                      // half-built character.
                                      openUpgrade('voice', {
                                        overrides: { name: m.title },
                                      })
                                    } else {
                                      setVoice(m.code)
                                      setVoiceModalOpen(false)
                                    }
                                  }}
                                  className={`px-4 py-2 cursor-pointer hover:bg-[#333] rounded-xl flex items-center justify-center ${
                                    m.code === voice
                                      ? 'font-medium text-white'
                                      : 'text-gray-300'
                                  }`}
                                >
                                  <p className="text-sm">{m.title}</p>
                                  {m.plan === 'Plus' && !isPlusOrHigher && (
                                    <FaLock className="ml-1" />
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="mt-1 text-gray-400 text-xs">
                        Your character's voice (you can change this later).
                      </p>
                    </div>

                    <div className="mb-3 flex items-center justify-center space-x-2">
                      <div className="w-1/2">
                        <label className="block text-white text-sm font-semibold mb-2 flex items-center">
                          <MdEmojiEmotions
                            color="#FFF"
                            className="mr-2"
                            size={12}
                          />
                          Emotions
                        </label>
                        <div className="flex items-center space-x-2 ">
                          <input
                            type="range"
                            min={0}
                            max={2}
                            value={emotions}
                            onChange={(e) =>
                              setEmotions(parseInt(e.target.value))
                            }
                            className="slider"
                          />
                          <div className="text-white w-24 font-semibold text-sm">
                            {getEmotionsString(emotions)}
                          </div>
                        </div>
                        <p className="mt-1 text-gray-400 text-xs">
                          This will affect their emotions, mood and photos.
                        </p>
                      </div>

                      <div className="w-1/2">
                        <label className="block text-white text-sm font-semibold mb-2 flex items-center">
                          <GiDress color="#FFF" className="mr-2" size={12} />
                          Openness
                        </label>
                        <div className="flex items-center space-x-2 ">
                          <input
                            type="range"
                            min={0}
                            max={2}
                            value={openness}
                            onChange={(e) =>
                              setOpenness(parseInt(e.target.value))
                            }
                            className="slider"
                          />
                          <div className="text-white w-24 font-semibold text-sm">
                            {getOpennessString(openness)}
                          </div>
                        </div>
                        <p className="mt-1 text-gray-400 text-xs">
                          This will affect how they respond to your messages and
                          actions.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : activeTab === 2 ? (
                  <div className="gap-x-4 width-full">
                    <div className="mb-3">
                      <label className="block text-white text-sm font-semibold mb-2 flex items-center">
                        <IoMdHeart color="#FFF" className="mr-2" size={12} />
                        Personality
                      </label>

                      <textarea
                        type="text"
                        id="personality"
                        placeholder="Enter personality"
                        value={personality}
                        maxLength={300}
                        onChange={(e) => setPersonality(e.target.value)}
                        className="border border-gray-600 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none h-24"
                        required
                      />

                      <p className="mt-1 text-gray-400 text-xs">
                        Select the personality of your character. This will
                        influence how your character will act.
                      </p>
                    </div>

                    <div className="mb-3  ">
                      <label className="block text-white text-sm font-semibold mb-2 flex items-center">
                        <FaTheaterMasks
                          color="#FFF"
                          className="mr-2"
                          size={12}
                        />
                        Scenario
                      </label>

                      <textarea
                        type="text"
                        id="scenario"
                        placeholder="Enter scenario"
                        value={scenario}
                        maxLength={300}
                        onChange={(e) => setScenario(e.target.value)}
                        className="border border-gray-600 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none h-24"
                        required
                      />

                      <p className="mt-1 text-gray-400 text-xs">
                        Select the roleplaying scenario. This will influence the
                        context of your conversations in Roleplay Mode.
                      </p>
                    </div>

                    <div className="mb-3  ">
                      <label className="block text-white text-sm font-semibold mb-2 flex items-center">
                        <MdChatBubble color="#FFF" className="mr-2" size={12} />
                        First Message
                      </label>

                      <textarea
                        type="text"
                        id="first message"
                        placeholder="Enter first message"
                        value={firstMessage}
                        maxLength={300}
                        onChange={(e) => setFirstMessage(e.target.value)}
                        className="border border-gray-600 rounded-md px-3 py-2 w-full text-white bg-[var(--background-bg-color)] focus:outline-none h-24"
                        required
                      />

                      <p className="mt-1 text-gray-400 text-xs">
                        Write what your character sends first. Or leave it empty
                        if you want to send the first message.
                      </p>
                    </div>
                  </div>
                ) : (
                  <p>Something went wrong...</p>
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
          }}
          className="border-t-2 border-gray-800"
        >
          <div className="p-4 bg-[var(--middle-bg-color)] rounded-md  pb-10">
            <div className="flex items-center justify-center">
              {/* ✅ FIX 1: show normal navigation for tab 0 and 1 */}
              {activeTab === 0 || activeTab === 1 ? (
                <div className="space-x-2 flex w-full">
                  {activeTab > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setActiveTab(activeTab - 1)
                      }}
                      className="w-full
                                px-4 py-3
                                bg-[var(--middle-bg-color)]
                                text-white font-bold
                                rounded-full
                                transition duration-200 ease-in-out
                                transform hover:scale-[1.02]
                                outline-none 
                                flex justify-center items-center"
                    >
                      <p>Back</p>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      // ✅ FIX 3: clamp to last tab
                      setActiveTab((prev) => Math.min(prev + 1, 2))
                    }}
                    className="w-full
                    px-4 py-3
                    bg-[var(--main-bg-color)]
                    text-white font-bold
                    rounded-full
                    transition duration-200 ease-in-out
                    transform hover:scale-[1.02]
                    outline-none 
                    flex justify-center items-center
                    disabled:opacity-50"
                    disabled={
                      (activeTab === 0 && !charImage) ||
                      (activeTab === 1 && !name)
                    }
                  >
                    <p>Next</p>
                  </button>
                </div>
              ) : continueOptions && activeTab === 0 ? (
                <div className="space-x-2 flex w-full">
                  <button
                    type="button"
                    onClick={() => {
                      if (!isPlusOrHigher && count > 0) {
                        setCountExceededModal(true)
                      } else {
                        setContinueOptions(false)
                        setImageSrc([])
                        setActiveTab(0)
                      }
                    }}
                    className={`
                    w-full
                    px-4 py-3
                    bg-[var(--middle-bg-color)]
                                text-white
                    font-bold
                    rounded-full
                    transition duration-200 ease-in-out
                    transform hover:scale-[1.02]
                    outline-none 
                    disabled:opacity-50
                    flex justify-center items-center
                    ${
                      isLoading || (faceTab === 'existing' && !charImage)
                        ? 'opacity-50'
                        : 'opacity-100'
                    }
                    `}
                    disabled={
                      isLoading || (faceTab === 'existing' && !charImage)
                    }
                  >
                    <FaUndo className="mr-2 text-lg" />
                    <p>Try Again</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab((prev) => Math.min(prev + 1, 2))
                    }}
                    className={`
                    w-full
                    px-4 py-3
                    bg-[var(--main-bg-color)]
                    text-white font-bold
                    rounded-full
                    transition duration-200 ease-in-out
                    transform hover:scale-[1.02]
                    outline-none 
                    disabled:opacity-50
                    flex justify-center items-center
                    ${isLoading ? 'opacity-50' : 'opacity-100'}
                    `}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <ClipLoader
                        color={'#FFF'}
                        loading={isLoading}
                        size={24}
                      />
                    ) : (
                      <>
                        <FaArrowRight className="mr-2 text-lg" />
                        <p>Personality</p>
                      </>
                    )}
                  </button>
                </div>
              ) : activeTab === 2 ? (
                <div className="space-x-2 flex w-full">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab(activeTab - 1)
                    }}
                    className="w-full
                                px-4 py-3
                                bg-[var(--middle-bg-color)]
                                text-white font-bold
                                rounded-full
                                transition duration-200 ease-in-out
                                transform hover:scale-[1.02]
                                outline-none 
                                flex justify-center items-center"
                  >
                    <p>Back</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setPostModalOpen(true)
                    }}
                    className={`
                    w-full
                    px-4 py-3
                    bg-[var(--main-bg-color)]
                    text-white font-bold
                    rounded-full
                    transition duration-200 ease-in-out
                    transform hover:scale-[1.02]
                    outline-none 
                    disabled:opacity-50
                    flex justify-center items-center
                    ${isLoading ? 'opacity-50' : 'opacity-100'}
                    `}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <ClipLoader
                        color={'#FFF'}
                        loading={isLoading}
                        size={24}
                      />
                    ) : (
                      <>
                        <FaArrowRight className="mr-2 text-lg" />
                        <p>Chat</p>
                      </>
                    )}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CharacterInputs
